// Walker-facing "Known Issues" section. Fetches active hazard reports
// for the current trail and renders each as a compact card with the
// human-readable category, description, and (optional) reporter photo.
//
// Empty state: reassuring "No known issues reported." card.
//
// Backend contract: GET /api/trails/{trail_id}/reports → { items: [{
//   report_id, category_label, description, image_url, created_at
// }] } — only active reports; resolved / dismissed are hidden.
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";
import { api, BACKEND_URL } from "../lib/api";
import { AchievementBadges, formatAuthorLine } from "../lib/achievements";
import HazardVoteButtons from "./HazardVoteButtons";

export default function KnownIssuesSection({ trailId }) {
  const q = useQuery({
    queryKey: ["trail-reports", trailId],
    enabled: !!trailId,
    queryFn: async () => (await api.get(`/trails/${trailId}/reports`)).data.items || [],
    staleTime: 1000 * 60 * 2,
  });

  const items = q.data || [];
  const showEmpty = !q.isLoading && items.length === 0;

  return (
    <section
      className="td-card p-5 sm:p-6 mt-5"
      data-testid="known-issues-section"
      data-state={q.isLoading ? "loading" : items.length > 0 ? "active" : "empty"}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-brand-primary" />
          <div className="td-section-label !mt-0">Known issues</div>
        </div>
        {items.length > 0 && (
          <span
            data-testid="known-issues-count"
            className="text-xs font-heading text-brand-primary bg-brand-primary/10 border border-brand-primary/30 rounded-full px-2 py-0.5"
          >
            {items.length} active
          </span>
        )}
      </div>

      {q.isLoading && (
        <div className="mt-3 flex items-center gap-2 text-sm text-brand-subtle">
          <Loader2 size={14} className="animate-spin" /> Checking recent reports…
        </div>
      )}

      {showEmpty && (
        <div
          data-testid="known-issues-empty"
          className="mt-3 rounded-2xl border border-brand-border/70 bg-brand-sand/60 px-4 py-3 flex items-center gap-3"
        >
          <span className="shrink-0 w-9 h-9 rounded-xl bg-brand-primary/10 text-brand-primary flex items-center justify-center">
            <ShieldCheck size={18} />
          </span>
          <div className="text-sm text-brand-text/90 leading-snug">
            No known issues reported. Spot something on your walk?{" "}
            <span className="text-brand-subtle">Tap "Report an issue" further down the page to let other walkers know.</span>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <ul className="mt-3 space-y-3" data-testid="known-issues-list">
          {items.map((r) => (
            <li
              key={r.report_id}
              data-testid={`known-issue-${r.report_id}`}
              className="rounded-2xl border border-amber-300/70 bg-amber-50 p-3 flex gap-3"
            >
              {r.image_url ? (
                <img
                  src={`${BACKEND_URL}${r.image_url}`}
                  alt={r.category_label}
                  loading="lazy"
                  className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover border border-amber-200"
                />
              ) : (
                <span className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center">
                  <AlertTriangle size={22} className="text-amber-700" />
                </span>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-heading text-sm text-amber-900 leading-tight" data-testid={`known-issue-category-${r.report_id}`}>
                  {r.category_label}
                </div>
                {r.description && (
                  <p className="text-xs sm:text-[13px] text-amber-900/85 mt-1 leading-snug whitespace-pre-line" data-testid={`known-issue-description-${r.report_id}`}>
                    {r.description}
                  </p>
                )}
                {(r.author_name || r.author_dog_name) && (
                  <div
                    className="text-[11px] text-amber-800/85 mt-1 flex items-center gap-1.5"
                    data-testid={`known-issue-author-${r.report_id}`}
                  >
                    <span>{formatAuthorLine(r.author_name, r.author_dog_name)}</span>
                    <AchievementBadges keys={r.author_achievements} size={12} className="text-amber-800" />
                  </div>
                )}
                {r.created_at && (
                  <div className="text-[11px] text-amber-800/70 mt-1.5">
                    {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                )}
                <HazardVoteButtons trailId={trailId} report={r} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
